import { useRef, useState } from 'react';
import { toast } from 'sonner';
import type { AxiosProgressEvent } from 'axios';
import type { UploadedFile } from '@r/types/index.ts';
import { apiMediasPost } from '@r/api/generated/client.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

interface UploadProgressEvent {
  file: string;
  progress: number;
}

interface UseUploadFileProps {
  defaultUploadedFiles?: UploadedFile[];
  onUploadProgress?: (event: UploadProgressEvent) => void;
  mediaType?: 'layout' | 'assets';
}

export function useUploadFile({
  defaultUploadedFiles = [],
  onUploadProgress,
  mediaType = 'layout'
}: UseUploadFileProps = {}) {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(defaultUploadedFiles);
  const [progresses, setProgresses] = useState<Record<string, number>>({});
  const [isUploading, setIsUploading] = useState(false);
  // Compteur d'appels `onUpload` en vol : plusieurs uploads concurrents (ex. une
  // colonne média par cible) ne doivent réinitialiser progresses/isUploading que
  // lorsque le dernier se termine, pas le premier.
  const inFlightUploadsRef = useRef(0);
  const showOrvalErrorToast = useOrvalErrorToast();

  async function onUpload(files: File[]) {
    inFlightUploadsRef.current += 1;
    setIsUploading(true);
    const results = await Promise.allSettled(files.map((file) => uploadFile(file)));

    const successfulUploads = results
      .filter(
        (result): result is PromiseFulfilledResult<UploadedFile> => result.status === 'fulfilled'
      )
      .map((result) => result.value);

    const failedUploads = results.filter(
      (result): result is PromiseRejectedResult => result.status === 'rejected'
    );

    if (successfulUploads.length) {
      setUploadedFiles((prev) => [...prev, ...successfulUploads]);
    }

    if (failedUploads.length) {
      for (const { reason: error } of failedUploads) {
        if (isOrvalErrorFromPath(error, 'post', '/medias')) {
          showOrvalErrorToast(error, 'File upload failed');
        } else {
          // Erreur d'un autre endpoint
          toast.error('An unexpected error occurred');
        }
      }
    }

    inFlightUploadsRef.current -= 1;
    if (inFlightUploadsRef.current === 0) {
      setProgresses({});
      setIsUploading(false);
    }
    return successfulUploads;
  }

  async function uploadFile(file: File): Promise<UploadedFile> {
    const media = await apiMediasPost(
      { file: file, type: mediaType },
      {
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && progressEvent.total > 0) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgresses((prev) => ({
              ...prev,
              [file.name]: percentCompleted
            }));
            onUploadProgress?.({ file: file.name, progress: percentCompleted });
          }
        }
      }
    );

    return {
      name: file.name,
      type: file.type,
      iri: media.iri as string,
      pathUri: media.pathUri as string
    };
  }

  return {
    onUpload,
    progresses,
    uploadedFiles,
    isUploading,
    setUploadedFiles
  };
}
